import { fromJS, List, Map } from 'immutable';

import isElectron from 'is-electron';
import HomeClient from 'flow/api-clients/home-client';
import createSocket, {
    closeTopConnection,
    probeTopConnection,
} from 'flow/api-clients/top-client';
import appConfig from 'app-config';
import { history } from 'flow/configure-store';
import EmbeddedType from 'flow/types/embedded-type';
import ClientsResource from 'flow/api/clients';
import ProvisioningResource from 'flow/api/provisioning';
import SessionResource from 'flow/api/session';
import Atg from 'flow/api/atg';
import ExtensionsResource from 'flow/api/extensions';
import FeatureFlagResource from 'flow/api/featureflag';
import CapabilityResource from 'flow/api/capability';
import AppStoreResource from 'flow/api/appstore';
import { isAllowedUrl } from 'flow/utils/geonex-api-utils';
import Actions from 'flow/actions';

import { disconnectApp } from 'flow/actions/navigate-actions';
import { appendConsentLog } from 'flow/utils/consent-log-utils';
import { generatePath } from 'flow/utils/router-utils';
import { routes } from 'flow/types/routes';
import {
    clearExtensionsActiveCalls,
    fetchExtensions,
    receiveExtension,
} from 'flow/actions/extension-actions';
import {
    fetchCustomerContactFields,
    fetchLoggedInCustomer,
} from 'flow/actions/customers-actions';
import {
    fetchQueues,
    fetchQueuesWithoutContact,
} from 'flow/actions/pbx-actions';
import { clearTopQueueCalls } from 'flow/actions/top-actions';
import {
    fetchChatMessages,
    fetchChatSessions,
} from 'flow/actions/chat-actions';
import { routeUserToOmnichannelErrand } from 'flow/actions/omnichannel-actions';
import { refreshMissedQueueCallsWidgetData } from 'flow/actions/PbxActions';
import { fetchGeoRestrictionEnabled } from 'flow/actions/geolocation-actions';
import { geoRestrictionIsDeployed } from 'flow/utils/geolocation-utils';
import {
    fetchCompanionConfig,
    loadHandoverToDesktop,
    loadUseAsCompanion,
} from 'flow/companion/companion-actions';
import { fetchPersonalAndSharedContacts } from 'flow/actions/contact-actions';
import throttle from 'lodash/throttle';

import { getTicketIdUiFriendlyFromTicketKey } from 'flow/utils/ticket-utils';
import {
    configureJsSIPLogs,
    createInitialUserAgent,
    loadPlayRingtoneSetting,
    setPhoneEnabled,
    stopUserAgent,
} from 'flow/actions/phone-actions';
import {
    androidFinishedLoading,
    iosFinishedLoading,
} from 'flow/actions/embedded-app-actions';
import { preloadSelectedRingtone } from 'flow/actions/ringtone-actions';
import { fetchCallWidgets } from 'flow/actions/run-url-actions';
import {
    fetchOpenTicketsAssignedToUserLatest,
    fetchTicketsBadgeCount,
    fetchEmailSignaturesEnabled,
    fetchTicketsTabIsEnforcedForUser,
    refreshTicketsData,
} from 'flow/actions/tickets-action';
import {
    setActiveMeetingViewAction,
    endMeeting,
} from 'flow/actions/meeting-actions';
import {
    extractName,
    generateRandomString,
    getContactAvatar,
    ISO_8601_MS_RESOLUTION,
} from 'flow/utils';
import { LocalesData } from 'flow/utils/locales-data';
import {
    APPSTORE_ENTRY_ID,
    getClientType,
    isAppCti,
    isMobileApp,
    isTeamsFullClient,
    isTeamsTypeIntegration,
    permissionNotificationsGranted,
    RightPaneWidth,
} from 'flow/utils/app-utils';
import { calculateBackoffTime, getRandomServer } from 'flow/utils/socket-utils';
import {
    addGeneralLogEntry,
    addLanguageTohtml,
    addOmniChannelLogEntry,
    addTopLogEntry,
    addUserPhoneLogEntry,
    clearCallWaitingFromLocalStorage,
    getAutoAnswerWithIpPhone,
    getAutomaticallySendClientLogs,
    getCallMethod,
    getCallWaitingEnabledFromLocalStorage,
    getInstanceId,
    getMakeCallsEnabled,
    getPreferredCallCanvasSize,
    hasCallWaitingLocalStorage,
    hasNetworkConnection,
    isChromeOrElectron,
    putAppInFocus,
    removePersonalInformationIfNewUser,
    saveApplicationLanguage,
    saveAutoAnswerWithIpPhone,
    saveCallMethod,
    saveMakeCallsEnabled,
    savePreferredCallCanvasSize,
    saveSpokenLanguage,
    setAutoLogin,
    setInstanceId,
    setLanguageDirection,
} from 'flow/utils/browser-utils';
import { DisplayMode } from 'flow/utils/rtc-utils';
import {
    getContactFromNumber,
    prettyFormatE164Number,
} from 'flow/utils/call-utils';
import {
    getIsLoggedIn,
    getOpenTopConnection,
    getOwnExtensionKey,
} from 'flow/selectors/selectors';
import { base64EncodeUTF8 } from 'flow/utils/transcode-utils';
import { appIsVisible } from 'flow/utils/notification-utils';
import { i18next } from 'flow/configure-locale';
import {
    fetchConnectedMediaDevices,
    loadHeadsetConf,
    loadPreferredAudioOutputId,
    loadNoiseSuppressionEngine,
    loadPreferredMicrophoneId,
    loadPreferredRingtoneAudioOutputId,
    loadPreferredVideoInputId,
} from 'flow/actions/headset-actions';
import DEFAULT_AVATAR_PNG from 'images/ic_telavox_default_avatar_60.png';
import { electronActions } from 'flow/utils/electron-utils';
import {
    cancelSendingLogsOnInterval,
    sendExistingUserLogsOnInterval,
} from 'flow/actions/client-log-actions';
import { getFaro } from 'flow/api-clients/faro-client';
import { getBrand, resetToEnvironmentBranding } from 'flow/brands/brands';
import { showCommunicationCanvas } from 'flow/selectors/call-selectors';
import { getExtension } from 'flow/selectors/ExtensionSelectors';
import { selectFeatureFlags } from 'flow/selectors/feature-flag-selectors';

import { isIntegration } from 'flow/utils/is-integration';
import { EVENT_STRINGS } from 'flow/utils/segment-utils';
import { segmentTrack } from 'flow/actions/segment-actions';
import { setPreventAppUnload } from 'flow/utils/appunload-helper';
import {
    fetchBranding,
    fetchBrandingLogoUrls,
} from 'flow/actions/brandingV2-actions';
import { IS_UAE_HOST } from 'flow/utils/browser-utils-TW';
import { isUaePassEnabled } from 'flow/constants/uae-pass/uae-pass-constants';
import { Capabilities } from 'flow/utils/capability-utils';
import { FeatureFlags } from 'flow/hooks/useFeatureFlags';
import { featureFlagCheck } from 'flow/utils/feature-flag-utils';
import { fetchKeyboardShortcuts } from 'flow/actions/keyboard-shortcuts-actions';
import {
    selectMountedChatSessionKey,
    selectPbxSectionIsMounted,
} from 'flow/selectors/app-selectors';
import { queryClient } from 'flow/contexts/query-client';
import { logoutFromUaePass } from 'flow/utils/uae-pass-logout';
import moment from 'moment';
import { updateIntegrationAccessToken } from './integration-access-actions';

// Throttle fetchQueues on TOP reconnection to prevent hammering the API
// during rapid reconnect cycles (e.g. unstable network). The first call
// goes through immediately; subsequent calls within 30s are dropped.
// trailing: false ensures no deferred call fires after the window expires.
const THIRTY_SECONDS = 30 * 1000;
const fetchQueuesThrottled = throttle(
    (dispatch) => dispatch(fetchQueuesWithoutContact()),
    THIRTY_SECONDS,
    { trailing: false },
);
import { fetchInitialSessionFromGeonex } from 'flow/utils/geonex-api-utils';
import { REGION_MISMATCH_LOGOUT } from 'flow/constants/region-lookup/login-data';

// ----------------------------------------------
// Constants pertaining to notification creation
// ----------------------------------------------

export const DEFAULT_AVAILABLE_NOTICE_TITLE = 'Unknown user';
export const DEFAULT_AVAILABLE_NOTICE_BODY = 'User is now available';
export const DEFAULT_CHAT_NOTICE_TITLE = 'Chat';
export const DEFAULT_CHANNEL_CHAT_NOTICE_TITLE = 'Channel chat';
export const DEFAULT_VIDEO_NOTICE_TITLE = 'Video';
export const DEFAULT_VOICEMAIL_NOTICE_TITLE = 'Voicemail';
const DEFAULT_INCOMING_CALL_TITLE = 'Incoming call';

export const DEFAULT_VIDEO_NOTICE_BODY = 'User wants to have a video chat';

// Resets all the global state. Note CLEAR_STATE intentionally keeps loginData
// (login status, not per-session data); logout clears it explicitly via
// clearLoginData. This is what lets the mid-boot new-user data wipe run without
// flipping isSuccessLogin / remounting Main into a second boot.
export const clearState = () => (dispatch) => {
    dispatch(stopUserAgent());
    cancelTopConnection();
    resetToEnvironmentBranding();
    dispatch({
        type: Actions.CLEAR_STATE,
    });
};

const calculateLayoutColumns = (adjustWidth) => {
    const width = window.innerWidth > 0 ? window.innerWidth : screen.width;

    const displayWidth = adjustWidth ? width - RightPaneWidth : width;

    if (displayWidth < 721) {
        return 1;
    } else if (displayWidth < 944) {
        return 2;
    } else {
        return 3;
    }
};

const isEmbedded = (state) => {
    return isMobileApp(state.app.get('embedded')) || isAppCti(state);
};

export const updateLayoutColumns =
    (dialpadOpen = false) =>
    (dispatch, getState) => {
        const { app } = getState();

        const adjustWidth =
            dialpadOpen ||
            (showCommunicationCanvas(getState()) &&
                app.get('callCanvasSize') === DisplayMode.THUMBNAIL.key);
        const layoutColumns = isEmbedded(getState())
            ? 1
            : calculateLayoutColumns(adjustWidth);

        if (app.get('layoutColumns') !== layoutColumns) {
            dispatch({
                type: Actions.LAYOUT_COLUMNS,
                data: layoutColumns,
            });
        }
    };

export const setVisibility = (visible) => ({
    type: Actions.APP_VISIBLE,
    data: visible,
});

export const setFocus = (focus) => ({
    type: Actions.APP_FOCUS,
    data: focus,
});

let clearPreventUnloadTimeout;
export const setPreventUnload = (prevent) => {
    setPreventAppUnload(prevent);

    if (clearPreventUnloadTimeout) {
        clearTimeout(clearPreventUnloadTimeout);
        clearPreventUnloadTimeout = null;
    }

    /**
     * This bad boy right here is to make sure we don't have preventUnload set to true because we've
     * expected 'beforeonload' event to trigger (see listener in context-component) without it actually happening
     */

    if (prevent) {
        setClearPreventUnloadTimer();
    }
};

const setClearPreventUnloadTimer = () => {
    clearPreventUnloadTimeout = setTimeout(() => {
        setPreventAppUnload(false);
    }, 500);
};

// After a successful login, navigate to a pending cold-start meeting if one was passed via
// args-event, otherwise navigate to the home route. Clears the meeting code after use so
// subsequent logins are not redirected to the same (possibly stale) meeting.
const navigateAfterLogin = (dispatch, getState) => {
    const electronArgs = getState().app.get('electronArgs');
    const meetingCode =
        isElectron() && electronArgs ? electronArgs.get('meetingCode') : null;
    if (meetingCode) {
        history.push(`/video?room=${meetingCode}`);
        dispatch({
            type: Actions.ELECTRON_ARGS,
            args: electronArgs.delete('meetingCode').toJS(),
        });
    } else {
        history.push('/');
    }
};

export const authServiceSignIn =
    (authToken, onError) => (dispatch, getState) => {
        if (authToken) {
            HomeClient.addAuthServiceToken(authToken);
            const version = appConfig.version;

            SessionResource.getLoggedInSession(
                {
                    onResult(/*json*/) {
                        dispatch(updateApplicationState('boot'));
                        dispatch(
                            receiveLoginData({
                                error: false,
                                waiting: false,
                            }),
                        );
                        navigateAfterLogin(dispatch, getState);
                    },
                    onError(/*status*/) {
                        const loginData = {
                            error: true,
                            networkFail: !hasNetworkConnection(),
                            serverFail: true,
                            waiting: false,
                        };
                        if (typeof onError === 'function') {
                            onError(loginData);
                        }
                        dispatch(receiveLoginData(loginData));
                    },
                    onClientError(req) {
                        const loginData = {
                            error: true,
                            networkFail: !hasNetworkConnection(),
                            loginFail: req.status === 401,
                            waiting: false,
                        };
                        if (typeof onError === 'function') {
                            onError(loginData);
                        }
                        dispatch(receiveLoginData(loginData));
                    },
                },
                'apps,live',
                version,
            );
        }
    };

export const signIn =
    (username, password, onSuccess, onError, accessToken = null) =>
    (dispatch, getState) => {
        if (accessToken) {
            HomeClient.accessToken = accessToken;
        } else {
            HomeClient.basic = base64EncodeUTF8(username + ':' + password);
        }

        dispatch(updateApplicationState('boot'));
        dispatch(receiveLoginData({ waiting: true }));

        /**
         * Remember me option will contain accessToken for login
         * So only if accessToken is not available
         * we will proceed with ATG login
         */
        if ((process.env.BRAND === 'etisalat' || IS_UAE_HOST) && !accessToken) {
            addGeneralLogEntry('User logged in via ATG');
            const AtgSignInDTO = {
                username,
                password,
            };
            Atg.signIn(
                {
                    onResult(/*json*/) {
                        onSuccess();
                        dispatch(
                            receiveLoginData({
                                error: false,
                                waiting: false,
                            }),
                        );
                        navigateAfterLogin(dispatch, getState);
                    },
                    onError(/*status*/) {
                        const loginData = {
                            error: true,
                            networkFail: !hasNetworkConnection(),
                            serverFail: true,
                            waiting: false,
                        };
                        if (typeof onError === 'function') {
                            onError(loginData);
                        }
                        dispatch(receiveLoginData(loginData));
                    },
                    onClientError(req) {
                        const loginData = {
                            error: true,
                            networkFail: !hasNetworkConnection(),
                            loginFail: req.status === 401,
                            waiting: false,
                        };
                        if (typeof onError === 'function') {
                            onError(loginData);
                        }
                        dispatch(receiveLoginData(loginData));
                    },
                },
                AtgSignInDTO,
            );
        } else {
            SessionResource.getLoggedInSession({
                onResult(/*json*/) {
                    onSuccess();
                    dispatch(
                        receiveLoginData({
                            error: false,
                            waiting: false,
                        }),
                    );
                    dispatch(
                        updateApplicationState(
                            'boot',
                            'boot-logged-in-session',
                        ),
                    );
                    navigateAfterLogin(dispatch, getState);
                },
                onError(/*status*/) {
                    const loginData = {
                        error: true,
                        networkFail: !hasNetworkConnection(),
                        serverFail: true,
                        waiting: false,
                        accessToken,
                    };
                    if (typeof onError === 'function') {
                        onError(loginData);
                    }
                    dispatch(receiveLoginData(loginData));
                },
                onClientError(req) {
                    const loginData = {
                        error: true,
                        networkFail: !hasNetworkConnection(),
                        loginFail: req.status === 401 && !accessToken,
                        waiting: false,
                        loginTokenFail: req.status === 401 && accessToken,
                        accessToken,
                    };
                    /**
                     * Delete expired integration access token from keystore
                     */
                    if (accessToken) {
                        electronActions.deleteIntegrationToken();
                    }
                    if (typeof onError === 'function') {
                        onError(loginData);
                    }
                    dispatch(receiveLoginData(loginData));
                },
            });
        }
    };

export const noPassword = () => (dispatch) => {
    const loginData = {
        error: true,
        networkFail: false,
        loginFail: true,
        waiting: false,
    };
    dispatch(receiveLoginData(loginData));
};

export const receiveLoginData = (loginData) => ({
    type: Actions.APP_LOGIN_DATA,
    data: loginData,
});

export const clearLoginData = {
    type: Actions.APP_LOGIN_DATA_CLEAR,
};

export const setLoggingOut = (isLoggingOut) => ({
    type: Actions.APP_LOG_OUT,
    data: isLoggingOut,
});

export const setAutoLoginWithAccessToken = (autoLoginAccessTokenId) => ({
    type: Actions.AUTO_LOGIN_WITH_ACCESS_TOKEN,
    autoLoginAccessTokenId,
});

// The keychain token can outlive its id in settings, so it is deleted even when the id is unknown.
const removeAutoLoginAccessToken =
    (extensionKey, id, onComplete) => async (dispatch) => {
        await electronActions.deleteIntegrationToken();

        if (!id || !extensionKey) {
            onComplete();
            return;
        }

        const integrationAccessDTO = {
            key: id,
            invalidatedDate: moment().format(ISO_8601_MS_RESOLUTION),
        };

        dispatch(
            updateIntegrationAccessToken(
                extensionKey,
                id,
                integrationAccessDTO,
                onComplete,
            ),
        );
    };

const sessionLogout =
    (embeddedType, path, clientType, instanceId) => (dispatch) => {
        setAutoLogin(false);
        HomeClient.basic = btoa('log:out');

        SessionResource.logout({
            onResult(/*json*/) {
                dispatch(disconnectApp(embeddedType)).finally(() => {
                    dispatch(clearState());
                    // clearState keeps loginData; logout must drop it so the
                    // user lands on SignIn (isSuccessLogin -> false).
                    dispatch(clearLoginData);
                    cancelSendingLogsOnInterval();

                    addGeneralLogEntry(
                        `User clicked on sign out button via: ${path} on client:·${clientType} with instanceId: ${instanceId} `,
                    );
                    if (path !== REGION_MISMATCH_LOGOUT) {
                        queryClient.clear();
                    }
                });
            },
            /* The only exit that skips CLEAR_STATE, so the flag has to be dropped by hand. */
            onError: (...args) => {
                dispatch(setLoggingOut(false));
                networkErrorDispatcher(dispatch)(...args);
            },
        });
    };

export const signOut = (path) => async (dispatch, getState) => {
    const { app } = getState();

    dispatch(endMeeting());

    /* Runs first: it needs the session alive, and before sessionLogout's HomeClient.basic claims the next request. */
    if (isUaePassEnabled) {
        /* This is the leg that can hold the app for seconds with nothing to show for it. */
        dispatch(setLoggingOut(true));
        await logoutFromUaePass(app.get('embedded'));
    }

    const notifications = app.get('notifications');
    const embeddedType = app.get('embedded');
    const autoLoginAccessTokenId = app?.getIn([
        'autoLogin',
        'autoLoginAccessTokenId',
    ]);

    const clientType = isElectron() ? 'Electron' : 'Webapp';
    const instanceId = getInstanceId() || null;
    notifications.forEach((notification) => notification.close());

    getFaro()?.api.resetUser();

    const extensionKey = app.getIn(['extension', 'key']);

    await dispatch(
        removeAutoLoginAccessToken(extensionKey, autoLoginAccessTokenId, () =>
            dispatch(sessionLogout(embeddedType, path, clientType, instanceId)),
        ),
    );
};

/**
 * Register the Top connection web socket.
 */
const registerTopConnection = (websocket) => ({
    type: Actions.REGISTER_TOP_CONNECTION,
    data: websocket,
});

// Guards against a race condition where multiple concurrent calls to
// createTopConnection each pass the getOpenTopConnection check before any
// of them has finished opening and registering a socket. This can happen on
// flaky networks where getOperatorServers is slow and a reconnect timer fires
// while a previous attempt is still in flight, resulting in multiple active
// WebSocket connections that all dispatch events to the same Redux store.
// Holds the attempt in flight, so that only that attempt can clear it: a late
// close or failure of an older attempt must not let a parallel one start.
let pendingTopConnection = null;

/**
 * Stop the Top connection and any attempt in flight. The Redux store only holds a connection once
 * it is open, so closing that one alone would let an attempt still connecting log in to Top after
 * the user has logged out.
 */
const cancelTopConnection = () => {
    pendingTopConnection = null;
    closeTopConnection('Logged out');
};

export const connectToTop =
    (retrieveLiveData = true) =>
    (dispatch) => {
        if (!appConfig.topEnabled) {
            return;
        }
        dispatch(createTopConnection(0, retrieveLiveData));
    };

/**
 * Make sure there is a working Top connection after something that may have broken it without
 * the socket noticing - the machine waking up, or the network coming back. A live connection is
 * only probed; a missing one is opened now rather than when a pending backoff timer fires.
 */
export const checkTopConnection = () => (dispatch, getState) => {
    const state = getState();
    // The mobile apps hold their own Top connection, so login never opens one for them.
    if (!getIsLoggedIn(state) || isMobileApp(state.app.get('embedded'))) {
        return;
    }
    addTopLogEntry('Checking the Top connection');
    clearTimeout(state.app.get('topRetryTimeoutId'));
    dispatch(connectToTop(true));
};

const createTopConnection =
    (numberOfAttempts = 0, retrieveLiveData = true) =>
    (dispatch, getState) => {
        const { app, extensions } = getState();

        const openTopConnection = getOpenTopConnection(getState());
        if (openTopConnection) {
            // Ask the open connection to prove it is alive. If nothing comes back it is given
            // up, and its close handler makes a reconnection attempt.
            probeTopConnection();
            return;
        }

        if (pendingTopConnection) {
            return;
        }

        const operator = app
            .get('operatorUsers', List())
            .find((user) => user.get('operatorType') === 'desktop');

        if (operator) {
            const extensionKey = getOwnExtensionKey(getState());
            const anumber = extensions.getIn([
                'extensions',
                extensionKey,
                'topRouteKey',
            ]);
            const username = operator.get('username');
            const password = operator.get('password');

            const attemptDelayedReconnect = (dispatch, getState) => {
                const isLoggedIn = getIsLoggedIn(getState());
                if (!isLoggedIn) {
                    return;
                }

                const backoffTime = calculateBackoffTime(numberOfAttempts);
                const timeoutId = setTimeout(() => {
                    dispatch(createTopConnection(++numberOfAttempts));
                    addTopLogEntry(
                        `Delayed reconnect attempt: ${numberOfAttempts}`,
                    );
                }, backoffTime);
                dispatch(setTopConnectionRetryTimeoutId(timeoutId));
            };

            const attempt = {};
            const isCurrentAttempt = () => pendingTopConnection === attempt;
            const settleAttempt = () => {
                if (isCurrentAttempt()) {
                    pendingTopConnection = null;
                }
            };
            pendingTopConnection = attempt;

            ProvisioningResource.getOperatorServers({
                onResult(json) {
                    // Cancelled (logged out) while waiting for the operator servers.
                    if (!isCurrentAttempt()) {
                        return;
                    }
                    // Re-check: a connection may have been established while
                    // waiting for the operator servers response.
                    if (getOpenTopConnection(getState())) {
                        settleAttempt();
                        return;
                    }
                    const server = getRandomServer(fromJS(json));
                    const host = server.get('host');
                    const webSocketPort = server.get('webSocketPort');
                    // Not guarded by isCurrentAttempt like its siblings: the attempt settles
                    // when the socket opens, so a guard would stop every reconnect after a
                    // normal disconnect. Sockets that are no longer current never get here -
                    // createSocket only calls this for the current one.
                    const onClose = () => {
                        settleAttempt();
                        dispatch(attemptDelayedReconnect);
                    };
                    createSocket(
                        'wss://' + host + ':' + webSocketPort,
                        anumber,
                        username,
                        password,
                        onClose,
                        retrieveLiveData,
                    )(dispatch, getState)
                        .then((socket) => {
                            if (!isCurrentAttempt()) {
                                return;
                            }
                            settleAttempt();
                            numberOfAttempts = 0;
                            dispatch(registerTopConnection(socket));
                        })
                        .catch(() => {
                            settleAttempt();
                            console.error('Socket error');
                        });
                },
                onError: () => {
                    if (!isCurrentAttempt()) {
                        return;
                    }
                    settleAttempt();
                    dispatch(attemptDelayedReconnect);
                },
            });
        } else {
            addTopLogEntry('Failed to start websocket, no operator user found');
            console.warn('Failed to start websocket, no operator user found');
        }
    };

export const getClientDTO = (token, version, isElectron) => {
    let description = 'Flowapp';

    if (isElectron) {
        const platform = process.platform === 'win32' ? 'Windows' : 'Mac';
        const userAgent = window.navigator
            ? window.navigator.appVersion
            : platform;
        description = `Flow desktop, ${userAgent}`;
    }

    const clientType = getClientType(isElectron);

    return {
        token,
        version,
        description,
        clientType,
    };
};

const registerClient = (key) => (dispatch) => {
    const clientDTO = getClientDTO(key, appConfig.version, isElectron());

    ClientsResource.register(
        {
            onResult(json) {
                dispatch(receiveClient(json));
            },
        },
        key,
        clientDTO,
    );
};

const handleSessionResult =
    (
        dispatch,
        initialStatusData,
        getState,
        embedded,
        onFailure,
        skipTopConnection,
    ) =>
    (session) => {
        if (initialStatusData) {
            const { regionData, isRegionCheckEnabled } = initialStatusData;
            const userRegion = session?.region;
            const isRegionMismatch =
                userRegion &&
                isRegionCheckEnabled &&
                regionData?.socialLoginType &&
                regionData?.region !== userRegion;

            if (isRegionMismatch) {
                dispatch(signOut(REGION_MISMATCH_LOGOUT));
                dispatch(updateApplicationState('region-mismatch'));
                return;
            }
        }

        if (session) {
            const extensionKey = session.extension.key;

            getFaro()?.api.setUser({ id: extensionKey });
            removePersonalInformationIfNewUser(extensionKey, () =>
                dispatch(clearState()),
            );

            const phoneEnabled = isPhoneEnabled(
                session.extension.sipInfo,
                getState(),
            );
            dispatch(enablePhone(phoneEnabled));
            dispatch(receiveOperatorUsers(session.operatorUsers));
            dispatch(receiveApplicationExtension(session.extension));
            dispatch(
                receiveTemporaryPasswordStatus(session.hasTemporaryPassword),
            );
            dispatch(fetchKeyboardShortcuts());
            dispatch(receiveExtension(session.extension));

            dispatch(fetchLoggedInCustomer(extensionKey))
                .then(() => {
                    const { customer } = getState();
                    const entityKey = getOwnExtensionKey(getState());
                    const loggedInCustomer = customer.get(entityKey);

                    if (IS_UAE_HOST) {
                        localStorage.setItem('videoConferenceWithSip', 'true');
                    }

                    // Don't render app until we know the brand
                    Promise.all([
                        fetchBranding(),
                        fetchBrandingLogoUrls(),
                        geoRestrictionIsDeployed()
                            ? dispatch(fetchGeoRestrictionEnabled())
                            : Promise.resolve(),
                    ]).then(() => {
                        if (
                            !embedded ||
                            isAppCti(getState()) ||
                            isTeamsFullClient(embedded) ||
                            phoneEnabled
                        ) {
                            dispatch(createInitialUserAgent());
                        }

                        dispatch(updateApplicationState('ready'));
                    });

                    // For every client, embedded or not and with or without a softphone: any
                    // call the user asks for honours "Make calls".
                    dispatch(loadMakeCallsEnabled());

                    if (
                        !embedded ||
                        isAppCti(getState()) ||
                        isTeamsFullClient(embedded)
                    ) {
                        if (isElectron()) {
                            electronActions.setLoggedInCustomer(
                                loggedInCustomer.get('key'),
                            );
                        }

                        if (phoneEnabled) {
                            dispatch(loadPlayRingtoneSetting());
                            configureJsSIPLogs();
                            dispatch(loadCallMethod());
                            dispatch(loadAutoAnswerWithIpPhone());
                        }
                    }

                    if (isIntegration()) {
                        dispatch(
                            segmentTrack(
                                EVENT_STRINGS.LOGGED_IN_CUSTOMER_LOADED,
                            ),
                        );
                    } else if (isTeamsFullClient(embedded)) {
                        dispatch(
                            segmentTrack(
                                EVENT_STRINGS.LOGGED_IN_FROM_TEAMS_FULL_VERSION,
                            ),
                        );
                    } else if (isTeamsTypeIntegration(embedded)) {
                        dispatch(
                            segmentTrack(
                                EVENT_STRINGS.LOGGED_IN_FROM_TEAMS_REGULAR_VERSION,
                            ),
                        );
                    }
                })
                .catch((/*error*/) => {
                    dispatch(
                        updateApplicationState('error', 'boot-fetch-customer'),
                    );
                    onFailure();
                });

            if (!skipTopConnection) {
                dispatch(connectToTop(false));
            }

            dispatch(registerClient(extensionKey));
            dispatch(fetchExtensions('contact'));
            dispatch(fetchPersonalAndSharedContacts());
            dispatch(fetchCallWidgets());
            dispatch(fetchLoggedInUserHasOmnichannelFeature);
            dispatch(fetchCapabilities);
            // Every companion code path is gated on this, so it has to be in state before a call
            // can arrive. Fails closed, so until it resolves the client behaves as it always did.
            dispatch(fetchCompanionConfig());
            // Local, per-install designation. Read from storage on every boot so the setting the
            // user picked on this machine survives a restart.
            dispatch(loadUseAsCompanion());
            // Per-surface, and defaults on: handover has to be in state before a call can arrive,
            // since it is what decides whether this client answers or hands over.
            dispatch(loadHandoverToDesktop());
            dispatch(
                fetchExtensionsCapabilities(
                    [
                        Capabilities.SOFTPHONE,
                        Capabilities.VIDEO,
                        Capabilities.CHAT,
                        Capabilities.TICKETS,
                    ].join(','),
                ),
            );
            dispatch(fetchCustomerContactFields);
            if (
                !embedded ||
                isAppCti(getState()) ||
                isTeamsFullClient(embedded)
            ) {
                dispatch(fetchFeatureFlagsForUser);
                dispatch(fetchAppStoreEntryForUser);
                dispatch(fetchTicketsTabIsEnforcedForUser()).then((value) => {
                    if (!value) return;
                    dispatch(fetchEmailSignaturesEnabled());
                    dispatch(fetchOpenTicketsAssignedToUserLatest);
                    dispatch(fetchTicketsBadgeCount());
                });
            }

            dispatch(fetchQueues());
            setInstanceId(session.extension.topRouteKey);

            //Set lang if electron or web
            if (
                !embedded ||
                isAppCti(getState()) ||
                isTeamsFullClient(embedded)
            ) {
                const response = updateLangConstants(
                    fromNoToNb(session.userLanguage),
                );
                dispatch(setAppLanguage(response));
                // There is a potential risk for race conditions here since some of the actions we
                // dispatch needs to be done before others. E.g. if the preloadSelectedRingtone isn't
                // done before getting user data so we can start receiving calls and we get an incoming
                // call during that time frame no ringtone will be heard.
                // We haven't had any problems with this yet, what we know about, but we should look at
                // a solution where we can guarantee that some actions happens before others. It would
                // be good to start using promises in our resources and actions when doing that.
                dispatch(preloadSelectedRingtone());
            }

            const spokenLanguage = fromNoToNb(session.spokenLanguage);
            saveSpokenLanguage(spokenLanguage);
            dispatch(setSpokenLanguage(spokenLanguage));
            dispatch(loadHeadsetConf(extensionKey));
            dispatch(loadPreferredMicrophoneId);
            dispatch(loadNoiseSuppressionEngine);
            dispatch(loadPreferredAudioOutputId);
            dispatch(loadPreferredRingtoneAudioOutputId);
            dispatch(loadPreferredVideoInputId);
            dispatch(fetchConnectedMediaDevices);
            dispatch(loadPreferredCallCanvasSize());
            dispatch(loadCallWaitingEnabled(extensionKey));
            const noUpdateVersion =
                process.env.NO_UPDATE === 'true'
                    ? ', No Update (NU) edition'
                    : '';

            if (getAutomaticallySendClientLogs()) {
                dispatch(sendExistingUserLogsOnInterval);
            }

            addGeneralLogEntry(
                `App booted, electron: ${isElectron()}${noUpdateVersion} brand: ${
                    getBrand().brandNameUIFriendly
                }`,
            );
        } else {
            dispatch(updateApplicationState('error', 'boot-logged-in-session'));
            onFailure();
        }
    };

export const prepareInitialState =
    (
        /** @type {{ isRegionCheckEnabled?: boolean, regionData?: any } | null} */ initialStatusData = null,
    ) =>
    (dispatch, getState) => {
        const { app } = getState();

        const version = appConfig.version;
        const embedded = app.get('embedded');
        const skipTopConnection = isMobileApp(embedded);
        const includes = 'apps,live';

        const { regionData, isRegionCheckEnabled } = initialStatusData || {};

        const onFailure = () => {
            dispatch(disconnectApp(embedded)).catch((error) => {
                addGeneralLogEntry(`Error in disconnect app: ${error}`);
            });
        };

        const hasDirectAuth =
            (HomeClient.basic && HomeClient.basic !== btoa('log:out')) ||
            !!HomeClient.accessToken;

        const loginData = app.get('loginData');
        const hasCompletedLogin =
            loginData &&
            loginData.get('waiting') === false &&
            loginData.get('error') === false;

        const shouldUseGeonex =
            isRegionCheckEnabled &&
            !regionData?.url &&
            !hasDirectAuth &&
            !hasCompletedLogin;

        const sessionPromise = shouldUseGeonex
            ? fetchInitialSessionFromGeonex(version, includes)
            : new Promise((resolve, reject) => {
                  SessionResource.getLoggedInSession(
                      {
                          onResult: resolve,
                          onError: reject,
                      },
                      includes,
                      version,
                  );
              });

        sessionPromise
            .then(
                handleSessionResult(
                    dispatch,
                    initialStatusData,
                    getState,
                    embedded,
                    onFailure,
                    skipTopConnection,
                ),
            )
            .catch(() => {
                dispatch(
                    updateApplicationState('error', 'boot-logged-in-session'),
                );
                onFailure();
            });
    };

const isPhoneEnabled = (sipInfo, state) => {
    if (!sipInfo) {
        return false;
    }

    if (!isChromeOrElectron() && !isAppCti(state)) {
        return false;
    }

    return appConfig.topEnabled;
};

const enablePhone = (shouldEnable) => (dispatch) => {
    if (isElectron()) {
        electronActions.sendPhoneEnabled(shouldEnable);
    }
    dispatch(setPhoneEnabled(shouldEnable));
};

export const acceptCookies = () => (dispatch) => {
    appendConsentLog('accepted');
    dispatch({
        type: Actions.ACCEPTED_COOKIES,
    });
};

export const rejectCookies = () => (dispatch) => {
    appendConsentLog('rejected');
    dispatch({
        type: Actions.REJECTED_COOKIES,
    });
};

export const saveConsentCategories = (categories) => (dispatch) => {
    appendConsentLog('partial');
    dispatch({
        type: Actions.SET_COOKIE_CONSENT_CATEGORIES,
        categories,
    });
};

const receiveClient = (json) => {
    return {
        type: Actions.RESULT_CLIENT_REGISTER,
        data: json,
    };
};

export const networkConnectionChange = (networkEstablished) => (dispatch) => {
    dispatch({
        type: Actions.NETWORK_CONNECTION_CHANGE,
        data: networkEstablished,
    });
};

export const networkErrorDispatcher = (dispatch) => () => {
    dispatch(
        setSnackbar({
            i18nextMessage: 'snackbar.error',
            key: 'networkerror',
        }),
    );
};

export const httpGetErrorDispatcher = () => () => {};

export const updateApplicationState = (state, error = null) => {
    return {
        type: Actions.APP_STATE,
        state: state,
        error: error,
    };
};

export const search = (text) => {
    return {
        type: Actions.SEARCH,
        searchText: text,
    };
};

const receiveApplicationExtension = (extension) => {
    return {
        type: Actions.RESULT_SESSION,
        data: extension,
    };
};

const receiveOperatorUsers = (operatorUsers) => ({
    type: Actions.RESULT_OPERATOR_USERS,
    data: operatorUsers,
});

/**
 * Spawn a notice that lets the user know a chat message has arrived. On clicking the notice,
 * close it and redirect to that conversation.
 */
export const spawnChatNotification =
    (sessionKey, contact, chatMessage, channelKey) => (dispatch, getState) => {
        const { chats, channels } = getState();

        const onClick = (notification) => {
            putAppInFocus();
            history.push(
                generatePath(routes['messages.id'], { id: sessionKey }),
            );
            notification.close();
        };

        const onClickChannel = (notification) => {
            putAppInFocus();
            history.push(
                generatePath(routes['pbx.channels.channelKey.chatSessionKey'], {
                    channelKey,
                    chatSessionKey: sessionKey,
                }),
            );
            notification.close();
        };

        const session = chats.getIn(['sessions', sessionKey]);

        //Regular chatsession
        if (session) {
            const isRoom = session.get('roomType') !== 'session';
            const name = session.get('description');

            const title =
                (isRoom && name ? name + ' - ' : '') +
                extractName(contact, DEFAULT_CHAT_NOTICE_TITLE);
            const icon = getContactAvatar(contact, DEFAULT_AVATAR_PNG);
            const body = chatMessage.get('title')
                ? 'NEW POST: ' + chatMessage.get('title')
                : chatMessage.get('message');

            dispatch(spawnNotification(title, body, icon, onClick));
        } else {
            const title = channels.getIn(
                ['channels', channelKey, 'channelName'],
                DEFAULT_CHANNEL_CHAT_NOTICE_TITLE,
            );
            const icon = getContactAvatar(contact, DEFAULT_AVATAR_PNG);
            const body = chatMessage.get('message');

            dispatch(spawnNotification(title, body, icon, onClickChannel));
        }
    };

/**
 * Spawn a notice that lets the user know that another user they're watching just became available.
 */
export const spawnAvailableNotification = (contact) => (dispatch) => {
    const onClick = (notification) => {
        putAppInFocus();
        notification.close();
    };

    const title = extractName(contact, DEFAULT_AVAILABLE_NOTICE_TITLE);
    const icon = getContactAvatar(contact, DEFAULT_AVATAR_PNG);

    dispatch(
        spawnNotification(
            title,
            DEFAULT_AVAILABLE_NOTICE_BODY,
            icon,
            onClick,
            true,
        ),
    );
};

/**
 * Spawn a notice that lets the user know that another user invited them to a video call.
 */
export const spawnVideoNotification = (contact) => (dispatch) => {
    const onClick = (notification) => {
        putAppInFocus();
        notification.close();
    };

    const title = extractName(contact, DEFAULT_VIDEO_NOTICE_TITLE);
    const icon = getContactAvatar(contact, DEFAULT_AVATAR_PNG);

    dispatch(
        spawnNotification(title, DEFAULT_VIDEO_NOTICE_BODY, icon, onClick),
    );
};

/**
 * Spawn a notice that lets the user know that they received a voicemail.
 */
export const spawnVoiceMessageNotification =
    (voiceMessageDTO) => (dispatch) => {
        const onClick = (notification) => {
            putAppInFocus();
            notification.close();
        };

        const title = DEFAULT_VOICEMAIL_NOTICE_TITLE;
        const body =
            i18next.t('call.voicemail.new') +
            ' ' +
            voiceMessageDTO.get('number');

        dispatch(spawnNotification(title, body, null, onClick));
    };

/**
 * Spawn a notice that lets the user know that the last agent logged out.
 */
export const spawnLastAgentNotification = (pushData) => (dispatch) => {
    const onClick = (notification) => {
        putAppInFocus();
        notification.close();
    };

    const title = pushData.description;
    const body = i18next.t('Notification.LastAgent.Body', {
        time: pushData.sent,
    });

    dispatch(spawnNotification(title, body, null, onClick));
};

export const spawnIncomingOmnichannelErrandNotification =
    (session, deepLinkId, description, channelType) => (dispatch) => {
        const onClick = (notification) => {
            addOmniChannelLogEntry('User clicked on omnichannel notification');
            try {
                addOmniChannelLogEntry('Answering omnichannel SIP invite');
                session.answer();
            } catch (error) {
                addOmniChannelLogEntry(
                    `Could not answer omnichannel SIP invite due to: ${error}`,
                );
            }
            dispatch(routeUserToOmnichannelErrand(deepLinkId, 'notification'));
            putAppInFocus();
            notification.close();
        };

        //\u{1f4ac} = speech balloon emoji
        //\u{1f4e8} = envelope emoji
        const title =
            channelType === 'whatsapp' || channelType === 'chat'
                ? '\u{1f4ac} New ticket'
                : '\u{1f4e8} New ticket';

        dispatch(spawnNotification(title, description, null, onClick));
    };

export const spawnOmnichannelPushNotificationReceived =
    (deeplink) => (dispatch) => {
        const onClick = (notification) => {
            addOmniChannelLogEntry('User clicked on omnichannel notification');
            dispatch(routeUserToOmnichannelErrand(deeplink, 'notification'));
            putAppInFocus();
            notification.close();
        };

        const title = 'Ticket assigned to you';
        const description = '';

        dispatch(spawnNotification(title, description, null, onClick));
    };

export const spawnTicketNotification =
    (
        ticketKey,
        notificationTitle,
        notificationDescription,
        icon,
        notificationKey,
    ) =>
    (dispatch) => {
        const onClick = (notification) => {
            history.push(
                generatePath(routes['tickets.ticketKey'], { ticketKey }),
            );
            putAppInFocus();
            notification.close();
        };

        dispatch(
            spawnNotification(
                notificationTitle,
                notificationDescription,
                icon,
                onClick,
                false,
                notificationKey,
            ),
        );
    };

export const spawnTicketInternalNoteReceivedNotification =
    (ticketKey, messageText, senderExtensionKey) => (dispatch, getState) => {
        const onClick = (notification) => {
            history.push(
                generatePath(routes['tickets.ticketKey'], { ticketKey }),
            );
            putAppInFocus();
            notification.close();
        };

        const senderExtension =
            getExtension(getState(), senderExtensionKey) || Map();
        const senderContact = senderExtension.get('contact');
        const senderName = extractName(senderContact);
        const ticketId = getTicketIdUiFriendlyFromTicketKey(ticketKey);

        const title = `${ticketId} - ${senderName}`;
        const body = messageText;
        const icon = getContactAvatar(senderContact, DEFAULT_AVATAR_PNG);

        dispatch(spawnNotification(title, body, icon, onClick));
    };

export const spawnIncomingCallNotification =
    (number, countryCode, queueDescription) => (dispatch, getState) => {
        if (appIsVisible(getState)) {
            return;
        }

        const { extensions, contacts } = getState();

        const onClick = (notification) => {
            putAppInFocus();
            notification.close();
        };

        const internalContacts = extensions
            .get('extensions')
            .map((ext) => ext.get('contact'))
            .toList();
        const externalContacts = contacts
            .get('externalContacts', Map())
            .toList();
        const thirdPartyContacts = contacts
            .get('socialIntegrationContacts', Map())
            .toList();
        const lookups = contacts.get('cachedLookups', Map());
        const ownContacts = internalContacts.concat(externalContacts);
        const allContacts = ownContacts.concat(thirdPartyContacts);
        const contact = getContactFromNumber(allContacts, lookups, number);

        const prettyNumber = prettyFormatE164Number(number, countryCode);
        const title = DEFAULT_INCOMING_CALL_TITLE;
        let queuePart = queueDescription
            ? i18next.t('callcontrol.transfered_call', {
                  CPartDescription: queueDescription,
              })
            : '';
        queuePart = queuePart
            ? queuePart[0].toLowerCase() + queuePart.slice(1)
            : '';
        const body = `${extractName(contact, prettyNumber)} ${queuePart}`;
        const icon = getContactAvatar(contact, DEFAULT_AVATAR_PNG);

        addUserPhoneLogEntry(
            `Spawning incoming call notification for ${number}`,
        );
        dispatch(spawnNotification(title, body, icon, onClick));
    };

/**
 * Spawn a notification with a content specified by the parameters. If a timeout is specified, the notification
 * closes after that time.
 */
export const spawnNotification =
    (
        title,
        body,
        icon,
        handleClick,
        requireInteraction = false,
        notificationKey,
    ) =>
    (dispatch) => {
        const inWindow = 'Notification' in window;

        if (
            !permissionNotificationsGranted(
                inWindow,
                inWindow ? Notification : undefined,
            )
        ) {
            return;
        }

        const key = notificationKey || generateRandomString();

        const decodeHtmlEntities = (str) => {
            const txt = document.createElement('textarea');
            txt.innerHTML = str;
            return txt.value;
        };

        const options = {
            icon: icon,
            body: decodeHtmlEntities(body),
            requireInteraction: requireInteraction,
        };

        const notification = new Notification(
            decodeHtmlEntities(title),
            options,
        );

        notification.onclick = () => handleClick(notification);
        notification.onclose = () => {
            dispatch({
                type: Actions.DELETE_NOTICE,
                key: key,
                notification: notification,
            });
        };

        dispatch({
            type: Actions.ADD_NOTICE,
            key: key,
            notification: notification,
        });
    };

export const registerElectronArgs = (args) => (dispatch) => {
    dispatch({
        type: Actions.ELECTRON_ARGS,
        args: args,
    });
};

export const setApplicationLanguage = (languageCode) => (dispatch) => {
    const lang = fromNbToNo(languageCode);
    const languageDTO = Map({ code: lang });

    SessionResource.updateUserLanguage(
        {
            onResult(languageDTO) {
                const languageCode = updateLangConstants(
                    fromNoToNb(languageDTO),
                );
                dispatch(setAppLanguage(languageCode));
            },
            onError: networkErrorDispatcher(dispatch),
        },
        languageDTO,
    );
};

const fromNoToNb = (languageDTO) => {
    return languageDTO.code === 'no' ? 'nb' : languageDTO.code;
};

const fromNbToNo = (languageCode) => {
    return languageCode === 'nb' ? 'no' : languageCode;
};

const setAppLanguage = (languageCode) => ({
    type: Actions.APP_LANGUAGE,
    data: languageCode,
});

const setSpokenLanguage = (languageDTO) => ({
    type: Actions.SPOKEN_LANGUAGE,
    data: languageDTO,
});

export const updateLangConstants = (languageCode) => {
    // A code can outlive its entry in LocalesData: it is read back from
    // localStorage without validation, and it also arrives from the backend as
    // session.userLanguage. Dereferencing undefined here takes down the login
    // screen on mount, where nothing can change the language any more. Fall
    // back to the language the markup and i18next already fall back to, and
    // store that, so a dead code is corrected rather than read again forever.
    //
    // hasOwn, not a bare lookup: LocalesData inherits from Object.prototype,
    // so 'constructor' and 'toString' would pass a truthiness check and then
    // give an undefined fileCode -- lang="undefined", the very thing this
    // guard exists to prevent.
    const resolvedCode = Object.hasOwn(LocalesData, languageCode)
        ? languageCode
        : 'en';
    const locale = LocalesData[resolvedCode];

    saveApplicationLanguage(resolvedCode);
    addLanguageTohtml(locale.fileCode);
    setLanguageDirection(locale);

    i18next.changeLanguage(locale.fileCode);
    electronActions.setLanguageForWindowsSpellChecker(
        locale.electronSpellCheck,
    );

    // Callers put this in the store. Handing back the raw code would leave
    // state holding a language the DOM and i18next are not using.
    return resolvedCode;
};

const receiveTemporaryPasswordStatus = (hasTemporaryPassword) => ({
    type: Actions.TEMPORARY_PASSWORD_STATUS,
    data: hasTemporaryPassword,
});

export const signalFinishedLoadingToMobiles = () => (dispatch, getState) => {
    const embedded = getState().app.get('embedded');
    if (EmbeddedType.IOS.is(embedded)) {
        dispatch(iosFinishedLoading());
    } else if (EmbeddedType.ANDROID.is(embedded)) {
        dispatch(androidFinishedLoading());
    }
};

const receiveCallMethod = (callMethod) => ({
    type: Actions.CALL_METHOD,
    callMethod: callMethod,
});

export const setCallMethod = (callMethod) => (dispatch) => {
    saveCallMethod(callMethod);
    dispatch(receiveCallMethod(callMethod));
};

const loadCallMethod = () => (dispatch) => {
    const callMethod = getCallMethod();
    dispatch(receiveCallMethod(callMethod));
};

export const receivePreferredCallCanvasSize = (size) => ({
    type: Actions.CALL_CANVAS_SIZE,
    size: size,
});

export const setPreferredCallCanvasSize = (size) => (dispatch) => {
    savePreferredCallCanvasSize(size);
    dispatch(receivePreferredCallCanvasSize(size));
};

export const loadPreferredCallCanvasSize = () => (dispatch) => {
    const size = getPreferredCallCanvasSize();
    dispatch(
        setActiveMeetingViewAction(
            DisplayMode.THUMBNAIL.is(size) ? DisplayMode.FULLSCREEN.key : size,
        ),
    );
    dispatch(receivePreferredCallCanvasSize(size));
};

const receiveAutoAnswerWithIpPhone = (autoAnswer) => ({
    type: Actions.AUTOANSWER_IP_PHONE,
    autoAnswer: autoAnswer,
});

export const setAutoAnswerWithIpPhone = (autoAnswer) => (dispatch) => {
    saveAutoAnswerWithIpPhone(autoAnswer);
    dispatch(receiveAutoAnswerWithIpPhone(autoAnswer));
};

const loadAutoAnswerWithIpPhone = () => (dispatch) => {
    const autoAnswer = getAutoAnswerWithIpPhone();
    dispatch(receiveAutoAnswerWithIpPhone(autoAnswer));
};

const receiveMakeCallsEnabled = (makeCallsEnabled) => ({
    type: Actions.MAKE_CALLS_ENABLED,
    makeCallsEnabled,
});

export const setMakeCallsEnabled = (makeCallsEnabled) => (dispatch) => {
    saveMakeCallsEnabled(makeCallsEnabled);
    dispatch(receiveMakeCallsEnabled(makeCallsEnabled));
};

const loadMakeCallsEnabled = () => (dispatch) => {
    dispatch(receiveMakeCallsEnabled(getMakeCallsEnabled()));
};

const synchCallWaiting = (extensionKey, callWaitingDTO) => {
    if (!hasCallWaitingLocalStorage()) {
        return;
    }
    const callWaitingLocalStorage = getCallWaitingEnabledFromLocalStorage();
    if (callWaitingLocalStorage !== callWaitingDTO.callWaiting) {
        callWaitingDTO.callWaiting = callWaitingLocalStorage;
        ExtensionsResource.setCallWaiting(
            {
                onResult() {
                    clearCallWaitingFromLocalStorage();
                },
                onError(error) {
                    console.error('Error when synching call waiting: ', error);
                },
                onClientError(error) {
                    console.error(
                        'Client Error when synching call waiting: ',
                        error,
                    );
                },
            },
            extensionKey,
            callWaitingDTO,
        );
    } else {
        clearCallWaitingFromLocalStorage();
    }
};

export const loadCallWaitingEnabled = (extensionKey) => (dispatch) => {
    ExtensionsResource.getCallWaiting(
        {
            onResult(callWaitingDTO) {
                synchCallWaiting(extensionKey, callWaitingDTO);
                dispatch({
                    type: Actions.SET_CALL_WAITING,
                    callWaiting: fromJS(callWaitingDTO),
                });
            },
            onError(error) {
                console.error('Error when fetching call waiting: ', error);
            },
            onClientError(error) {
                console.error(
                    'Client Error when fetching call waiting: ',
                    error,
                );
            },
        },
        extensionKey,
    );
};

const setTopConnectionRetryTimeoutId = (timeoutId) => ({
    type: Actions.TOP_RETRY_TIMEOUT_ID,
    timeoutId: timeoutId,
});

export const checkVersion = () => (dispatch) => {
    const version = appConfig.version;
    SessionResource.validateWebappVersion(
        {
            onResult(isAllowed) {
                dispatch({
                    type: Actions.APP_ALLOWED_VERSION,
                    data: isAllowed,
                });
            },
            onError(error) {
                console.error('Error when checking version: ', error);
            },
            onClientError(error) {
                console.error('Client Error when checking version: ', error);
            },
        },
        version,
    );
};

export const updateMissedCallState = () => ({
    type: Actions.ADD_MISSEDCALL_STATE,
});

export const clearMissedCallState = () => ({
    type: Actions.CLEAR_MISSEDCALL_STATE,
});

export const setMicPermission = (permission) => ({
    type: Actions.MIC_PERMISSION,
    data: permission,
});

export const setSnackbar = (data) => (dispatch) =>
    dispatch({
        type: Actions.ADD_SNACKBAR,
        message: data.message,
        i18nextMessage: data.i18nextMessage,
        i18nextMessageProps: data.i18nextMessageProps,
        hasCloseButton: data.hasCloseButton,
        key: data.key,
    });

export const removeSnackbar = () => ({
    type: Actions.REMOVE_SNACKBAR,
});

export const comeBackAPICallbacks = (dispatch, getState) => {
    const appFeatures = selectFeatureFlags(getState());
    const isThreadsEnabled = featureFlagCheck(
        appFeatures,
        FeatureFlags.THREADS,
    );
    const chatSessionIncludes = isThreadsEnabled
        ? ['contact', 'threads']
        : ['contact'];

    addGeneralLogEntry(
        'After top is reconnected, clear and fetch new live data from the API to update the state',
    );
    dispatch(clearTopQueueCalls());
    dispatch(clearExtensionsActiveCalls());

    // Get some real-time data to get rid of old call-states etc.
    dispatch(fetchExtensions('live'));
    dispatch(fetchChatSessions(...chatSessionIncludes)).catch(() =>
        console.error('Could not fetch chats'),
    );

    if (selectPbxSectionIsMounted(getState())) {
        fetchQueuesThrottled(dispatch);
    }

    // A chat on screen stopped being told about messages while we were away,
    // and the session list above only carries the newest one. Ask for the page
    // so the ones in between are filled in too.
    const mountedChatSessionKey = selectMountedChatSessionKey(getState());
    if (mountedChatSessionKey) {
        const messageIncludes = isThreadsEnabled
            ? ['contact', 'images', 'threads']
            : ['contact', 'images'];
        dispatch(
            fetchChatMessages(
                mountedChatSessionKey,
                null,
                false,
                ...messageIncludes,
            ),
        );
    }
    dispatch(refreshTicketsData());
    dispatch(refreshMissedQueueCallsWidgetData());
};

export const setAudioInitiated = (audioInitiated) => {
    return {
        type: Actions.SET_AUDIO_INITIATED,
        audioInitiated,
    };
};

const fetchLoggedInUserHasOmnichannelFeature = (dispatch) => {
    FeatureFlagResource.hasOmnichannelFeature({
        onResult(hasFeature) {
            dispatch(setLoggedInUserHasOmnichannelFeature(hasFeature));
        },
        onError: httpGetErrorDispatcher(dispatch),
    });
};

const setLoggedInUserHasOmnichannelFeature = (hasFeature) => ({
    type: Actions.SET_LOGGED_IN_USER_HAS_OMNICHANNEL_FEATURE,
    hasFeature,
});

export const fetchCapabilities = (dispatch) => {
    CapabilityResource.getCapabilities({
        onResult(json) {
            dispatch(resultCapabilities(json));
        },
        onError: httpGetErrorDispatcher(dispatch),
    });
};

export const fetchCapabilitiesForExtension = (extensionKey) => (dispatch) => {
    CapabilityResource.getExtensionCapabilities(
        {
            onResult(json) {
                dispatch(resultCapabilitiesForExtension(json, extensionKey));
            },
            onError: httpGetErrorDispatcher(dispatch),
        },
        extensionKey,
    );
};

export const fetchExtensionsCapabilities = (capabilities) => (dispatch) => {
    CapabilityResource.getExtensionsCapabilities(
        {
            onResult(json) {
                dispatch(resultExtensionsCapabilities(json));
            },
            onError: httpGetErrorDispatcher(dispatch),
        },
        '',
        capabilities,
    );
};

export const fetchFeatureFlagsForUser = (dispatch) => {
    return new Promise((resolve, reject) => {
        FeatureFlagResource.getFeatureFlag({
            onResult: (featureFlagDTO) => {
                dispatch(
                    setUserHasCtiIntegrationFeature(
                        !!featureFlagDTO.featureFlags.cti_integration,
                    ),
                );
                dispatch(
                    setLoggedInUserHasSupervisorFeature(
                        !!featureFlagDTO.featureFlags.supervisor_view,
                    ),
                );
                dispatch(
                    setLoggedInUserHasReturnBlindTransferFeature(
                        !!featureFlagDTO.featureFlags.return_blind_transfer,
                    ),
                );
                resolve();
            },
            onError: (error) => {
                httpGetErrorDispatcher(dispatch);
                reject(error);
            },
            onClientError: (error) => {
                httpGetErrorDispatcher(dispatch);
                reject(error);
            },
        });
    });
};

export const fetchAppStoreEntryForUser = (dispatch) => {
    return new Promise((resolve, reject) => {
        AppStoreResource.getEntry(
            {
                onResult: (appStoreEntryDTO) => {
                    const state = appStoreEntryDTO.state;
                    const hasCallLoggingApp =
                        appStoreEntryDTO.providerType == 'ipaas' &&
                        (state == 'installed' || state == 'broken_install');
                    dispatch(
                        setUserHasZendeskCallLoggingApp(hasCallLoggingApp),
                    );
                    resolve();
                },
                onError: (error) => {
                    httpGetErrorDispatcher(dispatch);
                    reject(error);
                },
                onClientError: () => {
                    // This endpoint returns 404 if the feature isn't found
                    dispatch(setUserHasZendeskCallLoggingApp(false));
                    resolve();
                },
            },
            APPSTORE_ENTRY_ID.ZENDESK_CALL_LOGGING,
        );
    });
};

const setUserHasZendeskCallLoggingApp = (hasFeature) => ({
    type: Actions.SET_LOGGED_IN_USER_HAS_ZENDESK_CALL_LOGGING_APP,
    hasFeature,
});

const setUserHasCtiIntegrationFeature = (hasFeature) => ({
    type: Actions.SET_LOGGED_IN_USER_HAS_CTI_INTEGRATION_FEATURE,
    hasFeature,
});

const setLoggedInUserHasSupervisorFeature = (hasFeature) => ({
    type: Actions.SET_LOGGED_IN_USER_HAS_SUPERVISOR_FEATURE,
    hasFeature,
});

const setLoggedInUserHasReturnBlindTransferFeature = (hasFeature) => ({
    type: Actions.SET_LOGGED_IN_USER_HAS_RETURN_BLIND_TRANSFER_FEATURE,
    hasFeature,
});

const resultCapabilities = (capabilities) => ({
    type: Actions.RESULT_USER_CAPABILITIES,
    capabilities: capabilities,
});

const resultCapabilitiesForExtension = (capabilities, extensionKey) => ({
    type: Actions.RESULT_EXTENSION_CAPABILITIES,
    capabilities: capabilities,
    key: extensionKey,
});

const resultExtensionsCapabilities = (json) => ({
    type: Actions.RESULT_EXTENSIONS_CAPABILITIES,
    data: json,
});

export const setPbxPageMounted = (queueKey) => ({
    type: Actions.SET_PBX_PAGE_MOUNTED,
    queueKey,
});

export const setPbxPageUnmounted = (queueKey) => ({
    type: Actions.SET_PBX_PAGE_UNMOUNTED,
    queueKey,
});

export const setPbxSectionMounted = (isMounted) => ({
    type: Actions.SET_PBX_SECTION_MOUNTED,
    isMounted,
});

export const updateRegionBasedAPIEndpoint = (regionData) => {
    const endpoint = regionData?.url;
    if (!endpoint) return;

    if (!isAllowedUrl(endpoint)) {
        console.error('Blocked attempt to set disallowed API host:', endpoint);
        return;
    }

    HomeClient.updateDefaultApiHost(endpoint);
};
